import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { CaseFeedbackSettingsState } from "@/CaseFeedback/store/CaseFeedbackSettingsState";
import router from "@/App/router";
import caseFeedbackService from "@/CaseFeedback/services/CaseFeedbackService";

const getDefaultFeedbackState: () => CaseFeedbackSettingsState = () => ({
  isSaveButtonEnabled: false,
  shouldNotDisturb: false,
});

export interface CaseFeedbackSettingsGetters extends GetterTree<CaseFeedbackSettingsState, unknown> {
  isSaveButtonEnabled: (state: CaseFeedbackSettingsState) => boolean;
  shouldNotDisturb: (state: CaseFeedbackSettingsState) => boolean;
}

export interface CaseFeedbackSettingsActions extends ActionTree<CaseFeedbackSettingsState, unknown> {
  onViewLoaded(context: ActionContext<CaseFeedbackSettingsState, unknown>);

  onSaveButtonClick(context: ActionContext<CaseFeedbackSettingsState, unknown>);

  onBackButtonClick(context: ActionContext<CaseFeedbackSettingsState, unknown>);

  doNotDisturbClicked(context: ActionContext<CaseFeedbackSettingsState, unknown>);

  saveDoNotDisturb(context: ActionContext<CaseFeedbackSettingsState, unknown>, shouldNotDisturb: boolean);
}

interface CaseFeedbackSettingsMutations extends MutationTree<CaseFeedbackSettingsState> {
  setDoNotDisturb(state: CaseFeedbackSettingsState, shouldNotDisturb: boolean);

  setSaveButtonEnabled(state: CaseFeedbackSettingsState, isSaveButtonEnabled: boolean);

  resetState(state: CaseFeedbackSettingsState);
}

export interface CaseFeedbackSettingsStore extends Module<CaseFeedbackSettingsState, unknown> {
  state: CaseFeedbackSettingsState;
  actions: CaseFeedbackSettingsActions;
  mutations: CaseFeedbackSettingsMutations;
  getters: CaseFeedbackSettingsGetters;
}

const getters: CaseFeedbackSettingsGetters = {
  isSaveButtonEnabled: (state: CaseFeedbackSettingsState) => state.isSaveButtonEnabled,
  shouldNotDisturb: (state: CaseFeedbackSettingsState) => state.shouldNotDisturb,
};

export const actions: CaseFeedbackSettingsActions = {
  onViewLoaded(context: ActionContext<CaseFeedbackSettingsState, unknown>) {
    const savedDoNotDisturb = caseFeedbackService.readDoNotDisturbPreference(context.rootGetters["Login/userId"]);
    context.commit("setDoNotDisturb", savedDoNotDisturb);
  },

  onSaveButtonClick(context: ActionContext<CaseFeedbackSettingsState, unknown>) {
    router.push({ name: "start" });
    context.dispatch("saveDoNotDisturb", context.state.shouldNotDisturb);
    context.commit("resetState");
  },

  onBackButtonClick(context: ActionContext<CaseFeedbackSettingsState, unknown>) {
    router.push({ name: "start" });
    context.commit("resetState");
  },

  doNotDisturbClicked(context: ActionContext<CaseFeedbackSettingsState, unknown>) {
    context.commit("setDoNotDisturb", !context.state.shouldNotDisturb);
    context.commit("setSaveButtonEnabled", !context.state.isSaveButtonEnabled);
  },

  saveDoNotDisturb(context: ActionContext<CaseFeedbackSettingsState, unknown>, shouldNotDisturb: boolean) {
    caseFeedbackService.saveDoNotDisturbPreference(context.rootGetters["Login/userId"], shouldNotDisturb);
  },
};

const mutations: CaseFeedbackSettingsMutations = {
  setDoNotDisturb(state: CaseFeedbackSettingsState, shouldNotDisturb: boolean) {
    state.shouldNotDisturb = shouldNotDisturb;
  },
  setSaveButtonEnabled(state: CaseFeedbackSettingsState, isSaveButtonEnabled: boolean) {
    state.isSaveButtonEnabled = isSaveButtonEnabled;
  },
  resetState(state: CaseFeedbackSettingsState) {
    const initialState = getDefaultFeedbackState();
    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
};

const store: CaseFeedbackSettingsStore = {
  namespaced: true,
  state: getDefaultFeedbackState(),
  getters,
  actions,
  mutations,
};

export default store;
